import React, { useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';

interface SyncAnalysis {
  totalAsesores: number;
  totalAsociados: number;
  asesoresConFirebase: number;
  asociadosConFirebase: number;
  asesoresSinEmail: number;
  asociadosSinEmail: number;
  emailsDuplicados: string[];
  registrosHuerfanos: any[];
}

export default function DataSyncAnalyzer() {
  const { user } = useUser();
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState<SyncAnalysis | null>(null);
  const [cleaning, setCleaning] = useState(false);

  // Solo admins
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="alert alert-danger">
        <h6>Acceso Denegado</h6>
        <p>Solo administradores pueden ejecutar análisis de sincronización.</p>
      </div>
    );
  }

  const analyzeDataSync = async () => {
    setAnalyzing(true);
    setResults(null);

    try {
      console.log('🔍 Analizando sincronización de datos...');

      // Obtener todas las colecciones
      const [asesoresSnap, asociadosSnap] = await Promise.all([
        getDocs(collection(db, 'asesor')),
        getDocs(collection(db, 'asociado'))
      ]);

      const asesores = asesoresSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const asociados = asociadosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Analizar emails
      const emailsAsesores = asesores.map((a: any) => a.correo).filter(Boolean);
      const emailsAsociados = asociados.map((a: any) => a.correo).filter(Boolean);
      const todosLosEmails = [...emailsAsesores, ...emailsAsociados];
      
      // Encontrar emails duplicados
      const emailCounts = new Map();
      todosLosEmails.forEach(email => {
        const emailLower = email.toLowerCase().trim();
        emailCounts.set(emailLower, (emailCounts.get(emailLower) || 0) + 1);
      });
      
      const emailsDuplicados = Array.from(emailCounts.entries())
        .filter(([_, count]) => count > 1)
        .map(([email, _]) => email);

      // Analizar Firebase UIDs
      const asesoresConFirebase = asesores.filter((a: any) => a.firebase_uid).length;
      const asociadosConFirebase = asociados.filter((a: any) => a.firebase_uid).length;

      // Analizar emails inválidos
      const asesoresSinEmail = asesores.filter((a: any) => 
        !a.correo || !a.correo.includes('@') || a.correo.length < 5
      ).length;
      
      const asociadosSinEmail = asociados.filter((a: any) => 
        !a.correo || !a.correo.includes('@') || a.correo.length < 5
      ).length;

      // Encontrar registros huérfanos (sin email válido y sin Firebase UID)
      const registrosHuerfanos = [
        ...asesores.filter((a: any) => !a.firebase_uid && (!a.correo || !a.correo.includes('@'))),
        ...asociados.filter((a: any) => !a.firebase_uid && (!a.correo || !a.correo.includes('@')))
      ];

      const analysis: SyncAnalysis = {
        totalAsesores: asesores.length,
        totalAsociados: asociados.length,
        asesoresConFirebase,
        asociadosConFirebase,
        asesoresSinEmail,
        asociadosSinEmail,
        emailsDuplicados,
        registrosHuerfanos
      };

      setResults(analysis);
      console.log('✅ Análisis completado:', analysis);

    } catch (error) {
      console.error('❌ Error en análisis:', error);
      Swal.fire('Error', 'No se pudo completar el análisis.', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const cleanOrphanedRecords = async () => {
    if (!results || results.registrosHuerfanos.length === 0) return;

    const confirm = await Swal.fire({
      title: 'Limpiar Registros Huérfanos',
      text: `Se eliminarán ${results.registrosHuerfanos.length} registros sin email válido ni Firebase Auth.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, limpiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    setCleaning(true);
    try {
      let deleted = 0;
      for (const registro of results.registrosHuerfanos) {
        const collectionName = registro.asesor_id ? 'asesor' : 'asociado';
        await deleteDoc(doc(db, collectionName, registro.id));
        deleted++;
      }

      Swal.fire('Éxito', `${deleted} registros huérfanos eliminados.`, 'success');
      
      // Re-analizar después de limpieza
      await analyzeDataSync();

    } catch (error) {
      console.error('Error limpiando registros:', error);
      Swal.fire('Error', 'No se pudieron eliminar todos los registros.', 'error');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="data-sync-analyzer">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-sync me-2"></i>
            Análisis de Sincronización Firebase Auth ↔ Colecciones
          </h5>
        </div>
        <div className="card-body">
          
          {!results && (
            <div className="text-center">
              <p className="text-muted mb-4">
                Este análisis comparará los datos en las colecciones de Firestore 
                con las cuentas de Firebase Authentication para identificar inconsistencias.
              </p>
              <button
                className="btn btn-primary"
                onClick={analyzeDataSync}
                disabled={analyzing}
              >
                {analyzing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analizando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Ejecutar Análisis
                  </>
                )}
              </button>
            </div>
          )}

          {results && (
            <div className="sync-results">
              {/* Resumen de Sincronización */}
              <div className="alert alert-info">
                <h6><i className="fas fa-info-circle me-2"></i>Resumen de Sincronización</h6>
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary">Asesores (Afiliados)</h6>
                    <ul>
                      <li><strong>Total en colección:</strong> {results.totalAsesores}</li>
                      <li><strong>Con Firebase Auth:</strong> {results.asesoresConFirebase}</li>
                      <li><strong>Sin email válido:</strong> {results.asesoresSinEmail}</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-info">Asociados</h6>
                    <ul>
                      <li><strong>Total en colección:</strong> {results.totalAsociados}</li>
                      <li><strong>Con Firebase Auth:</strong> {results.asociadosConFirebase}</li>
                      <li><strong>Sin email válido:</strong> {results.asociadosSinEmail}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Estadísticas Comparativas */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card text-center bg-primary text-white">
                    <div className="card-body">
                      <h4>{results.totalAsesores + results.totalAsociados}</h4>
                      <p className="mb-0">Total Registros</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card text-center bg-success text-white">
                    <div className="card-body">
                      <h4>{results.asesoresConFirebase + results.asociadosConFirebase}</h4>
                      <p className="mb-0">Con Firebase Auth</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card text-center bg-warning text-white">
                    <div className="card-body">
                      <h4>{results.asesoresSinEmail + results.asociadosSinEmail}</h4>
                      <p className="mb-0">Sin Email Válido</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card text-center bg-danger text-white">
                    <div className="card-body">
                      <h4>{results.registrosHuerfanos.length}</h4>
                      <p className="mb-0">Registros Huérfanos</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emails Duplicados */}
              {results.emailsDuplicados.length > 0 && (
                <div className="alert alert-warning">
                  <h6><i className="fas fa-exclamation-triangle me-2"></i>Emails Duplicados Detectados</h6>
                  <p><strong>{results.emailsDuplicados.length}</strong> emails están duplicados:</p>
                  <div className="small">
                    {results.emailsDuplicados.slice(0, 10).map((email, index) => (
                      <span key={index} className="badge bg-warning me-1 mb-1">{email}</span>
                    ))}
                    {results.emailsDuplicados.length > 10 && (
                      <span className="text-muted">... y {results.emailsDuplicados.length - 10} más</span>
                    )}
                  </div>
                </div>
              )}

              {/* Explicación de Discrepancias */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6><i className="fas fa-lightbulb me-2"></i>Explicación de Discrepancias</h6>
                </div>
                <div className="card-body">
                  <div className="alert alert-secondary">
                    <h6>¿Por qué Firebase Auth tiene menos usuarios?</h6>
                    <ol>
                      <li><strong>Filtro de emails:</strong> La migración solo crea cuentas Firebase para usuarios con emails válidos</li>
                      <li><strong>Emails duplicados:</strong> Firebase no permite emails duplicados, solo crea una cuenta por email</li>
                      <li><strong>Emails inválidos:</strong> Registros sin email o con formato incorrecto no obtienen cuenta Firebase</li>
                      <li><strong>Fallos en creación:</strong> Algunos emails pueden fallar durante el proceso de creación</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Acciones Recomendadas */}
              <div className="card">
                <div className="card-header">
                  <h6><i className="fas fa-cogs me-2"></i>Acciones Recomendadas</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-success">Mantener (Datos Válidos)</h6>
                      <ul>
                        <li>{results.asesoresConFirebase + results.asociadosConFirebase} registros con Firebase Auth</li>
                        <li>Estos tienen emails válidos y cuentas funcionales</li>
                        <li>Son los únicos que pueden hacer login</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-danger">Limpiar (Datos Problemáticos)</h6>
                      <ul>
                        <li>{results.registrosHuerfanos.length} registros sin email ni Firebase Auth</li>
                        <li>{results.emailsDuplicados.length} emails duplicados</li>
                        <li>Registros que nunca podrán acceder al sistema</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-outline-primary"
                        onClick={analyzeDataSync}
                        disabled={analyzing}
                      >
                        <i className="fas fa-sync me-2"></i>
                        Re-analizar
                      </button>
                      
                      {results.registrosHuerfanos.length > 0 && (
                        <button
                          className="btn btn-outline-danger"
                          onClick={cleanOrphanedRecords}
                          disabled={cleaning}
                        >
                          {cleaning ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Limpiando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-trash me-2"></i>
                              Limpiar Huérfanos ({results.registrosHuerfanos.length})
                            </>
                          )}
                        </button>
                      )}
                      
                      <button
                        className="btn btn-outline-info"
                        onClick={() => window.open('/password-reset-manager', '_blank')}
                      >
                        <i className="fas fa-key me-2"></i>
                        Gestionar Credenciales
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recomendación Final */}
              <div className="alert alert-success mt-4">
                <h6><i className="fas fa-check-circle me-2"></i>Recomendación</h6>
                <p className="mb-0">
                  <strong>Limpiar registros huérfanos</strong> mejorará la consistencia del dashboard 
                  y eliminará confusión en los conteos. Los usuarios con emails válidos se mantendrán intactos.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}